/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 05
 *   DB_空亡规则 (Sheet47, A1:F7 — 6 audited records)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_空亡规则 dataset status = AUTHORITATIVE_DATASET_COMPLETE (6 rows).
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL.
 *     (Batch 01 DB_天干 + DB_地支; Batch 02 DB_藏干; Batch 03 DB_十神;
 *     Batch 04 DB_60甲子; Batch 05 DB_空亡规则. Any remaining DB tables
 *     required by later Sheet27/28/29/30 batches are NOT_STARTED.)
 *
 * SCOPE (STRICT)
 *   Entire Sheet47 UsedRange A1:F7 is authoritative — 42/42 non-empty
 *   cells (Row1 headers + Row2..7 six data records). There are no
 *   out-of-scope areas inside A1:F7.
 *
 * ISOLATION
 *   Does NOT write into `src/engine/referenceData.js`. Production
 *   ReferenceData registry continues to hold only MOCK_POC placeholders.
 *   Does NOT wire into the main V2.9.9 pipeline.
 *   Does NOT modify M02 Batch 01/02/03/04 behaviour. Batch 01 DB_地支
 *   is imported ONLY for E/F cross-validation in the runner — not as a
 *   substitute for the audited Excel evidence stored here.
 *
 * SHEET27 B90 DEPENDENCY (documented, NOT implemented in production)
 *   Excel formula (audited):
 *     =IF(OR($B$37 = VLOOKUP(<band>, DB_空亡规则!$A$2:$F$7, 5, FALSE()),
 *           $B$37 = VLOOKUP(<band>, DB_空亡规则!$A$2:$F$7, 6, FALSE())),
 *         <two-character output>, "")
 *     where <band> = IF($B$51<=10, 1, IF($B$51<=20, 2, IF($B$51<=30, 3,
 *                       IF($B$51<=40, 4, IF($B$51<=50, 5, 6)))))
 *   The final two-character TRUE output has NOT yet been Unicode-audited
 *   in the Batch05 evidence. This module intentionally exposes
 *   `xunXuHaoBandFromB51(b51)` and `kongWangBranchesByXunXuHao(...)` so
 *   the lookup semantics can be tested WITHOUT hard-coding the B90 final
 *   output text. The Sheet27 B90 runtime rule remains OUT OF SCOPE until
 *   its exact output text is separately audited.
 *
 * UNICODE POLICY (MIGRATION_RULES.md → Encoding Warning)
 *   Every Chinese literal is constructed from its audited AscW/Hex code
 *   point via `String.fromCodePoint(...)`. No direct Chinese glyph
 *   appears in code — a comment on each constant states the canonical
 *   glyph for reader convenience.
 *
 * LOOKUP CONTRACT
 *   Exact-match Unicode-safe / integer equality. Missing / malformed /
 *   out-of-range keys throw `MISSING_REFERENCE_KEY` (M02 Batch 01–04
 *   convention). Invalid input types throw `TypeError`. No fuzzy match,
 *   no normalization, no silent fallback.
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Byte-wise identical output
 *   on every run. Freeze at dataset + rows + per-row + headers level.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── 天干 · 甲 (only stem used in 旬首干支) ───────────────────────────────
const CH_JIA = String.fromCodePoint(30002); // 甲

// ── 地支 (12) — audited code points ────────────────────────────────────
const CH_ZI   = String.fromCodePoint(23376); // 子
const CH_CHOU = String.fromCodePoint(19985); // 丑
const CH_YIN  = String.fromCodePoint(23493); // 寅
const CH_MAO  = String.fromCodePoint(21359); // 卯
const CH_CHEN = String.fromCodePoint(36784); // 辰
const CH_SI   = String.fromCodePoint(24051); // 巳
const CH_WU_BR= String.fromCodePoint(21320); // 午
const CH_WEI  = String.fromCodePoint(26410); // 未
const CH_SHEN = String.fromCodePoint(30003); // 申
const CH_YOU  = String.fromCodePoint(37193); // 酉
const CH_XU   = String.fromCodePoint(25100); // 戌
const CH_HAI  = String.fromCodePoint(20133); // 亥

// ── 旬首干支 (6) — audited Unicode code-point pairs ────────────────────
const GZ_JIA_ZI   = CH_JIA + CH_ZI;   // 甲子   [30002,23376]
const GZ_JIA_XU   = CH_JIA + CH_XU;   // 甲戌   [30002,25100]
const GZ_JIA_SHEN = CH_JIA + CH_SHEN; // 甲申   [30002,30003]
const GZ_JIA_WU   = CH_JIA + CH_WU_BR;// 甲午   [30002,21320]
const GZ_JIA_CHEN = CH_JIA + CH_CHEN; // 甲辰   [30002,36784]
const GZ_JIA_YIN  = CH_JIA + CH_YIN;  // 甲寅   [30002,23493]

// ── Headers (audited Unicode) ──────────────────────────────────────────
const HEADER_XUN_XU_HAO    = String.fromCodePoint(26092, 24207, 21495);              // 旬序号
const HEADER_XUN_SHOU_GZ   = String.fromCodePoint(26092, 39318, 24178, 25903);       // 旬首干支
const HEADER_QI_SHI_XU_HAO = String.fromCodePoint(36215, 22987, 24207, 21495);       // 起始序号
const HEADER_JIE_SHU_XU_HAO= String.fromCodePoint(32467, 26463, 24207, 21495);       // 结束序号
const HEADER_KONG_WANG_1   = String.fromCodePoint(31354, 20129, 25903, 49);          // 空亡支1
const HEADER_KONG_WANG_2   = String.fromCodePoint(31354, 20129, 25903, 50);          // 空亡支2

// ── 6 authoritative records (Excel Sheet47!A2:F7) ──────────────────────
const ROWS = Object.freeze([
  Object.freeze({ xunXuHao: 1, xunShouGanZhi: GZ_JIA_ZI,   qiShiXuHao:  1, jieShuXuHao: 10, kongWangZhi1: CH_XU,   kongWangZhi2: CH_HAI }), // 甲子 · 1..10 · 戌/亥
  Object.freeze({ xunXuHao: 2, xunShouGanZhi: GZ_JIA_XU,   qiShiXuHao: 11, jieShuXuHao: 20, kongWangZhi1: CH_SHEN, kongWangZhi2: CH_YOU }), // 甲戌 · 11..20 · 申/酉
  Object.freeze({ xunXuHao: 3, xunShouGanZhi: GZ_JIA_SHEN, qiShiXuHao: 21, jieShuXuHao: 30, kongWangZhi1: CH_WU_BR,kongWangZhi2: CH_WEI }), // 甲申 · 21..30 · 午/未
  Object.freeze({ xunXuHao: 4, xunShouGanZhi: GZ_JIA_WU,   qiShiXuHao: 31, jieShuXuHao: 40, kongWangZhi1: CH_CHEN, kongWangZhi2: CH_SI  }), // 甲午 · 31..40 · 辰/巳
  Object.freeze({ xunXuHao: 5, xunShouGanZhi: GZ_JIA_CHEN, qiShiXuHao: 41, jieShuXuHao: 50, kongWangZhi1: CH_YIN,  kongWangZhi2: CH_MAO }), // 甲辰 · 41..50 · 寅/卯
  Object.freeze({ xunXuHao: 6, xunShouGanZhi: GZ_JIA_YIN,  qiShiXuHao: 51, jieShuXuHao: 60, kongWangZhi1: CH_ZI,   kongWangZhi2: CH_CHOU}), // 甲寅 · 51..60 · 子/丑
]);

/**
 * DB_空亡规则 — 6-row 旬 → 空亡支 lookup (Sheet47 A1:F7).
 * Frozen at every level. All rows are the AUTHORITATIVE Excel evidence.
 */
export const DB_KONG_WANG_GUI_ZE = Object.freeze({
  codeName: "Sheet47",
  worksheetIndex: 47,
  sheetName: String.fromCodePoint(
    0x44, 0x42, 0x5F, 31354, 20129, 35268, 21017
  ), // DB_空亡规则
  sheetNameUnicode: [68, 66, 95, 31354, 20129, 35268, 21017],
  excelUsedRange: "A1:F7",
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master Value2 + AscW extraction",
  auditedInBatch: "M02_BATCH_05",
  countATotal: 42,        // A1:F7
  countADataOnly: 36,     // A2:F7
  headerCount: 6,
  recordCount: 6,
  headers: Object.freeze({
    A: HEADER_XUN_XU_HAO,       // 旬序号
    B: HEADER_XUN_SHOU_GZ,      // 旬首干支
    C: HEADER_QI_SHI_XU_HAO,    // 起始序号
    D: HEADER_JIE_SHU_XU_HAO,   // 结束序号
    E: HEADER_KONG_WANG_1,      // 空亡支1
    F: HEADER_KONG_WANG_2,      // 空亡支2
  }),
  rows: ROWS,
});

// ── Lookup API ─────────────────────────────────────────────────────────

function throwMissingKey(column, key) {
  const err = new Error(
    `[M02·DB_空亡规则] ${column} key ${JSON.stringify(key)} not found in ` +
      "AUTHORITATIVE_DATASET_COMPLETE (6 audited rows). No fuzzy match " +
      "or substitution is performed."
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = "DB_空亡规则";
  err.column = column;
  err.key = key;
  throw err;
}

/**
 * Exact-match row lookup by 旬序号 (1..6). Returns the full frozen row.
 * Throws MISSING_REFERENCE_KEY for out-of-range integers or TypeError
 * for non-integer input.
 */
export function kongWangRuleByXunXuHao(xunXuHao) {
  if (typeof xunXuHao !== "number" || !Number.isInteger(xunXuHao)) {
    throw new TypeError("[M02·DB_空亡规则] xunXuHao must be an integer");
  }
  const row = ROWS.find((r) => r.xunXuHao === xunXuHao);
  if (!row) throwMissingKey("旬序号", xunXuHao);
  return row;
}

/**
 * Convenience: return the 空亡 branches `{ kongWangZhi1, kongWangZhi2 }`
 * (audited 地支 glyphs) for a given 旬序号.
 */
export function kongWangBranchesByXunXuHao(xunXuHao) {
  const row = kongWangRuleByXunXuHao(xunXuHao);
  return Object.freeze({
    kongWangZhi1: row.kongWangZhi1,
    kongWangZhi2: row.kongWangZhi2,
  });
}

/**
 * Optional lookup by 旬首干支 (Column B). Returns the full frozen row.
 * Throws MISSING_REFERENCE_KEY for non-audited keys or TypeError for
 * non-string input.
 */
export function kongWangRuleByXunShouGanZhi(xunShouGanZhi) {
  if (typeof xunShouGanZhi !== "string") {
    throw new TypeError(
      "[M02·DB_空亡规则] xunShouGanZhi must be a string"
    );
  }
  const row = ROWS.find((r) => r.xunShouGanZhi === xunShouGanZhi);
  if (!row) throwMissingKey("旬首干支", xunShouGanZhi);
  return row;
}

/**
 * Sheet27 B90 band helper (audited semantics; DOES NOT emit the B90
 * final TRUE output text — that string is not audited in Batch 05).
 *
 * Excel semantics:
 *   IF(B51<=10, 1, IF(B51<=20, 2, IF(B51<=30, 3,
 *   IF(B51<=40, 4, IF(B51<=50, 5, 6)))))
 *
 * The mapping is deterministic for any real number input. Callers may
 * subsequently use `kongWangBranchesByXunXuHao(band)` to obtain
 * (空亡支1, 空亡支2), which corresponds to VLOOKUP columns 5 and 6 of
 * `DB_空亡规则!$A$2:$F$7`.
 *
 * @param {number} b51 The Sheet27 B51 value (numeric; typically 1..60).
 * @returns {number} 旬序号 in {1,2,3,4,5,6}
 */
export function xunXuHaoBandFromB51(b51) {
  if (typeof b51 !== "number" || Number.isNaN(b51)) {
    throw new TypeError("[M02·DB_空亡规则] b51 must be a finite number");
  }
  if (b51 <= 10) return 1;
  if (b51 <= 20) return 2;
  if (b51 <= 30) return 3;
  if (b51 <= 40) return 4;
  if (b51 <= 50) return 5;
  return 6;
}
